// typescript
export type ScrollOptions = {
    attempts?: number; // number of retries (default 1)
    headerOffsetFallback?: number; // fallback header offset in px (default 160)
    retryDelayMs?: number; // delay between attempts (default 220)
    align?: "top" | "middle"; // default "top" (keep previous behavior); "middle" centers element
};

const isScrollable = (el: Element | null) => {
    if (!el || !(el instanceof HTMLElement)) return false;
    const style = getComputedStyle(el);
    const overflowY = style.overflowY;
    return (overflowY === "auto" || overflowY === "scroll" || overflowY === "overlay") && el.scrollHeight > el.clientHeight;
};

const findScrollableAncestor = (el: Element | null): Element | null => {
    let cur: Element | null = el?.parentElement ?? null;
    while (cur) {
        if (isScrollable(cur)) return cur;
        cur = cur.parentElement;
    }
    return document.scrollingElement || document.documentElement;
};

const detectTopCoverOffset = (fallback = 160) => {
    try {
        const x = Math.floor(window.innerWidth / 2);
        const topElem = document.elementFromPoint(x, 0);
        if (!topElem || !(topElem instanceof HTMLElement)) return fallback;
        const rect = topElem.getBoundingClientRect();
        const style = getComputedStyle(topElem);
        const pos = style.position;
        if ((pos === "fixed" || pos === "sticky") && rect.bottom > 0) return Math.ceil(rect.bottom);
        if (topElem.tagName.toLowerCase() === "header" && rect.bottom > 0) return Math.ceil(rect.bottom);
    } catch {
        // ignore
    }
    return fallback;
};

/**
 * Scroll `target` into view so its top (default) or center (when align === "middle") appears just below any fixed/sticky header.
 * Does a measured smooth scroll and one optional corrective pass if needed.
 * Returns when scrolling (including the retry) completes.
 */
export async function scrollElementIntoViewWithOffset(target: HTMLElement, opts?: ScrollOptions): Promise<void> {
    const attempts = Math.max(0, (opts?.attempts ?? 1));
    const fallback = opts?.headerOffsetFallback ?? 160;
    const retryDelay = opts?.retryDelayMs ?? 220;
    const align = opts?.align ?? "top";

    const clamp = (value: number, min = 0, max = Number.POSITIVE_INFINITY) => Math.min(max, Math.max(min, value));

    const doScrollOnce = (targetEl: HTMLElement, headerOffset: number, scrollable: Element | null, smooth = true) => {
        const rect = targetEl.getBoundingClientRect();
        if (scrollable && scrollable !== document.scrollingElement && scrollable !== document.documentElement) {
            const anc = scrollable as HTMLElement;
            const ancRect = anc.getBoundingClientRect();
            const relativeTop = rect.top - ancRect.top;
            let desired = Math.floor(anc.scrollTop + relativeTop - headerOffset);

            if (align === "middle") {
                const ancViewport = anc.clientHeight;
                desired = Math.floor(anc.scrollTop + relativeTop - headerOffset - (ancViewport / 2) + (rect.height / 2));
            }

            const maxScroll = Math.max(0, anc.scrollHeight - anc.clientHeight);
            desired = clamp(desired, 0, maxScroll);

            try {
                anc.scrollTo({ top: desired, behavior: smooth ? "smooth" : "auto" });
            } catch {
                anc.scrollTop = desired;
            }
        } else {
            // page-level scroll
            const viewportH = window.innerHeight;
            let pageY = Math.floor(rect.top + window.pageYOffset - headerOffset);

            if (align === "middle") {
                pageY = Math.floor(rect.top + window.pageYOffset - headerOffset - (viewportH / 2) + (rect.height / 2));
            }

            const maxPage = Math.max(0, (document.scrollingElement as HTMLElement | null)?.scrollHeight ? ((document.scrollingElement as HTMLElement).scrollHeight - viewportH) : Math.max(document.documentElement.scrollHeight - viewportH, 0));
            pageY = clamp(pageY, 0, maxPage);

            try {
                window.scrollTo({ top: pageY, behavior: smooth ? "smooth" : "auto" });
            } catch {
                window.scrollTo(0, pageY);
            }
        }
    };

    return new Promise((resolve) => {
        const attemptScroll = (remainingAttempts: number) => {
            requestAnimationFrame(() => {
                try {
                    const headerOffset = detectTopCoverOffset(fallback);
                    const scrollable = findScrollableAncestor(target);
                    doScrollOnce(target, headerOffset, scrollable, true);

                    if (remainingAttempts > 0) {
                        // allow smooth scroll to run, then re-check and correct if needed
                        setTimeout(() => {
                            try {
                                const rectAfter = target.getBoundingClientRect();
                                const headerOffsetAfter = detectTopCoverOffset(fallback);
                                const visibleTop = rectAfter.top;
                                const desiredTop = headerOffsetAfter + 4; // small gap when aligning to top

                                if (align === "middle") {
                                    const viewportCenter = Math.floor(window.innerHeight / 2);
                                    const targetCenter = Math.floor(rectAfter.top + rectAfter.height / 2);
                                    if (Math.abs(targetCenter - viewportCenter) > 8) {
                                        doScrollOnce(target, headerOffsetAfter, scrollable, false);
                                    }
                                } else {
                                    if (visibleTop < desiredTop || visibleTop > desiredTop + 8) {
                                        // corrective immediate scroll (no animation)
                                        doScrollOnce(target, headerOffsetAfter, scrollable, false);
                                    }
                                }
                            } catch {
                                // ignore
                            } finally {
                                resolve();
                            }
                        }, retryDelay);
                    } else {
                        // no retry requested -> resolve after a short delay
                        setTimeout(() => resolve(), Math.min(150, retryDelay));
                    }
                } catch {
                    resolve();
                }
            });
        };

        attemptScroll(attempts);
    });
}
